import { useEffect, useRef } from 'react';

interface ResizeMessage {
  type: string;
  data: {
    height: number;
  };
}

export const useIframeResize = (
  elementRef: React.RefObject<HTMLElement | null>,
  enabled: boolean = true
) => {
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (
      !enabled ||
      window.self === window.top ||
      elementRef === null ||
      !elementRef.current
    ) {
      return;
    }

    let lastHeight = 0;
    let timeoutId: NodeJS.Timeout;

    const sendHeight = () => {
      if (!elementRef.current) return;

      const height = elementRef.current.scrollHeight;

      if (Math.abs(height - lastHeight) > 5) {
        lastHeight = height;
        const message: ResizeMessage = {
          type: 'resize',
          data: {
            height: height,
          },
        };

        window.parent.postMessage(JSON.stringify(message), '*');
      }
    };

    const debouncedSendHeight = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(sendHeight, 100);
    };

    sendHeight();

    observerRef.current = new ResizeObserver(() => {
      debouncedSendHeight();
    });

    observerRef.current.observe(elementRef.current);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [elementRef, enabled]);
};

interface IframeResizerProps {
  children: React.ReactNode;
  enabled?: boolean;
}

export const IframeResizer: React.FC<IframeResizerProps> = ({
  children,
  enabled = true,
}) => {
  const appMainRef = useRef<HTMLDivElement>(null);

  useIframeResize(appMainRef, enabled);

  return (
    <div
      ref={appMainRef}
      id="appMain"
      style={{ width: '100%', height: '100%' }}
    >
      {children}
    </div>
  );
};
